import { Flex, Select } from 'antd';
import translations from './OutputQuickSelect.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import styled from 'styled-components';
import { colorLabel } from '@atlas-frontend/design-tokens';
import {
  GroupDescriptionOption,
  GroupDescriptionOptions,
  SystemClassifierItemDto,
  SystemClassifierOption,
  TimeSeriesOutputType,
  useSystemClassifierItemsBy,
  useSystemClassifierSelectOptions,
} from '@atlas-frontend/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getDefaultSourceDataVersionId } from './TimeSeriesOutputsTable';

const { addTranslations, t } = i18nNsFactory('output-quick-select');
addTranslations(translations);

export const OutputQuickSelect: React.FC<{
  options: GroupDescriptionOptions;
  sourceDataVersionOptions?: SystemClassifierOption[];
  isLoading?: boolean;
}> = ({ options, sourceDataVersionOptions, isLoading }) => {
  const { PROCESSING_PREDEFINED_OUTPUTS: processingPredefinedOutputs } =
    useSystemClassifierSelectOptions(['PROCESSING_PREDEFINED_OUTPUTS']);
  const processingPredefinedOutputsById = useSystemClassifierItemsBy([
    'PROCESSING_PREDEFINED_OUTPUTS',
  ])['PROCESSING_PREDEFINED_OUTPUTS'];
  const [selectedQuickSelect, setSelectedQuickSelect] = useState<
    string | undefined
  >();
  const { setValue } = useFormContext<{
    outputs: {
      groupDescriptionRef: string;
      datePeriod: null;
      sourceDataVersionId?: number;
    }[];
  }>();

  const outputsCount = useWatch({ name: 'outputs.length' });
  const groupDescriptionRefs = useWatch({
    name: 'outputs',
    compute: (values: TimeSeriesOutputType[]) =>
      values?.map((v) => v.groupDescriptionRef) || [],
  });
  const isApplyingQuickSelect = useRef(false);

  // Reset quick select when output size or groupDescriptionRef values change
  useEffect(() => {
    if (isApplyingQuickSelect.current) {
      // Skip reset if change was triggered by quick select itself
      isApplyingQuickSelect.current = false;
      return;
    }

    setSelectedQuickSelect(undefined);
  }, [outputsCount, groupDescriptionRefs]);

  const handleQuickSelectChange = useCallback(
    (id?: string) => {
      if (!id) {
        setSelectedQuickSelect(undefined);
        return;
      }

      const selectedItem = processingPredefinedOutputsById.get(id);
      if (!selectedItem) return;

      const quickSelectOutputs = getQuickSelectOutputs(
        parseOutputCodes(selectedItem),
        options.flat(),
        sourceDataVersionOptions,
      );
      if (quickSelectOutputs.length > 0) {
        isApplyingQuickSelect.current = true;
        setValue('outputs', quickSelectOutputs);
      }

      setSelectedQuickSelect(id);
    },
    [
      options,
      processingPredefinedOutputsById,
      setValue,
      sourceDataVersionOptions,
    ],
  );

  return (
    <Flex vertical gap="small">
      <StyledLabel htmlFor="output-quick-select">{t('label')}</StyledLabel>
      <Select
        id="output-quick-select"
        value={selectedQuickSelect}
        options={processingPredefinedOutputs}
        style={{ maxWidth: '40%' }}
        allowClear
        onChange={handleQuickSelectChange}
        disabled={isLoading}
      />
    </Flex>
  );
};

const parseOutputCodes = (item: SystemClassifierItemDto) => {
  if (!item.parameters) return [];
  return item.parameters.split(',').map((code) => code.trim());
};

const getQuickSelectOutputs = (
  codes: string[],
  groupDescriptionOptions: GroupDescriptionOption[],
  sourceDataVersionOptions?: SystemClassifierOption[],
) =>
  codes
    .map((code) =>
      groupDescriptionOptions.find((option) => option.code === code),
    )
    .filter((option): option is GroupDescriptionOption => !!option)
    .map((option) => ({
      groupDescriptionRef: option.value.toString(),
      datePeriod: null,
      sourceDataVersionId: getDefaultSourceDataVersionId(
        groupDescriptionOptions,
        sourceDataVersionOptions || [],
        option.value,
      ),
      comment: '',
    }));

const StyledLabel = styled.label`
  color: ${colorLabel};
`;
